import type {
  FetchKlipyGifsPageOptions,
  KlipyAsset,
  KlipyGif,
  KlipyGifsPageResult,
  KlipyListResponse,
  KlipyTier,
} from '../types';
import { KLIPY_SEARCH_PER_PAGE, KLIPY_TRENDING_PER_PAGE } from '../constants';

const KLIPY_API = 'https://api.klipy.com/api/v1';
const KLIPY_JSON_NUMERIC_ID_FIELD_PATTERN = String.raw`"id"\s*:\s*([0-9]+)`;

export function parseKlipyResponseJsonText(text: string): unknown {
  const prepared = text.replace(
    new RegExp(KLIPY_JSON_NUMERIC_ID_FIELD_PATTERN, 'g'),
    (match, digits: string) => {
      try {
        const n = BigInt(digits);

        if (
          n > BigInt(Number.MAX_SAFE_INTEGER) ||
          n < BigInt(Number.MIN_SAFE_INTEGER)
        ) {
          return `"id":"${digits}"`;
        }
      } catch {
        return match;
      }

      return match;
    }
  );

  return JSON.parse(prepared) as unknown;
}

function klipyListJsonOrThrow(body: unknown): KlipyListResponse {
  if (typeof body !== 'object' || body === null) {
    throw new Error('GIF request failed');
  }

  const json = body as KlipyListResponse;

  if (json.result === false) {
    throw new Error('GIF request failed');
  }

  return json;
}

function coerceKlipyGifList(value: unknown): KlipyGif[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((el) => ({
    ...(el as KlipyGif),
    id: String((el as KlipyGif).id),
  }));
}

export function parseKlipyGifsPageBody(
  body: unknown,
  fallbackPage: number
): KlipyGifsPageResult {
  const json = klipyListJsonOrThrow(body);
  const payload = json.data;
  const gifs = coerceKlipyGifList(payload?.data);
  const hasNext = Boolean(payload?.has_next);
  const currentPage =
    typeof payload?.current_page === 'number'
      ? payload.current_page
      : fallbackPage;
  return { gifs, hasNext, currentPage };
}

function pickUrl(
  gif: KlipyGif,
  tiers: KlipyTier[],
  asset: KlipyAsset
): string | undefined {
  const file = gif.file;

  if (!file) {
    return undefined;
  }

  for (const tier of tiers) {
    const url = file[tier]?.[asset]?.url;

    if (url) {
      return url;
    }
  }

  return undefined;
}

export function getKlipyGifThumbUrl(gif: KlipyGif): string | undefined {
  return pickUrl(gif, ['sm', 'xs', 'md'], 'gif');
}

export function getKlipyGifChatDisplayUrl(gif: KlipyGif): string | undefined {
  return pickUrl(gif, ['md', 'sm', 'hd'], 'gif');
}

export function getKlipyGifChatVideoMp4Url(gif: KlipyGif): string | undefined {
  return pickUrl(gif, ['md', 'sm', 'hd'], 'mp4');
}

export function getKlipyGifChatVideoWebmUrl(gif: KlipyGif): string | undefined {
  return pickUrl(gif, ['md', 'sm', 'hd'], 'webm');
}

export function getKlipyGifPosterUrl(gif: KlipyGif): string | undefined {
  return pickUrl(gif, ['md', 'sm'], 'jpg') ?? pickUrl(gif, ['md'], 'gif');
}

export function getKlipyGifsListUrl(
  options: FetchKlipyGifsPageOptions
): string {
  const { apiKey, path: gifsPath, query, page } = options;
  const locale =
    typeof navigator !== 'undefined' && navigator.language
      ? navigator.language.replace('-', '_')
      : 'en_US';

  const perPage =
    gifsPath === 'trending' ? KLIPY_TRENDING_PER_PAGE : KLIPY_SEARCH_PER_PAGE;

  const params = new URLSearchParams({
    per_page: String(perPage),
    rating: 'g',
    locale,
  });

  if (gifsPath === 'search') {
    params.set('q', query);
    params.set('page', String(page));
  }

  return `${KLIPY_API}/${apiKey}/gifs/${gifsPath}?${params.toString()}`;
}

export async function fetchKlipyGifItemById(
  apiKey: string,
  gifId: string,
  signal?: AbortSignal
): Promise<KlipyGif | null> {
  const url = `${KLIPY_API}/${apiKey}/gifs/items?ids=${encodeURIComponent(
    gifId.trim()
  )}`;

  const res = await fetch(url, { signal });

  if (!res.ok) {
    throw new Error('GIF item request failed');
  }

  const text = await res.text();
  const json = klipyListJsonOrThrow(parseKlipyResponseJsonText(text));
  const items = coerceKlipyGifList(json.data?.data);
  const normalizedId = gifId.trim();

  return items.find((item) => item.id === normalizedId) ?? items[0] ?? null;
}

export function loadKlipyGifsPage(
  options: FetchKlipyGifsPageOptions
): Promise<KlipyGifsPageResult> {
  const { page, signal } = options;
  return fetch(getKlipyGifsListUrl(options), { signal }).then((res) => {
    if (!res.ok) {
      throw new Error('GIF request failed');
    }
    return res
      .text()
      .then((text) =>
        parseKlipyGifsPageBody(parseKlipyResponseJsonText(text), page)
      );
  });
}
